import { JSONRPCErrorCode } from 'json-rpc-2.0';

import { fg } from '@atlaskit/platform-feature-flags/fg';

import {
	type CommandConstructor,
	type CommandHandler,
	type CommandProperties,
	type CommandResult,
	type ExtractCommandProperties,
	type ExtractCommandResult,
} from '../command/bridge-api-command-types';
import { emitter } from '../events/bridge-events-emitter';
import { type Transport } from '../transport/bridge-api-transport-types';
import { rpcError, type RPCRequest, type RPCResponse, rpcSuccess } from './bridge-api-rpc-message';

/* eslint-disable no-console */

/**
 * Maintains all handlers for commands passed into the bridge API.
 *
 * I.e., if JS context 1 invokes a command which JS context 2 is meant to handle, {@link RequestHandler}
 * is responsible for connecting the {@link RPCRequest} to the appropriate command handler in JS context 2. It
 * will also communicate the result of the request over the provided {@link transport} protocol.
 */
export class RequestHandler {
	/**
	 * A mapping from clientId:commandName -> handlers for those commands.
	 *
	 * @description We enforce only 1 handler per clientId+command pair, so that callees can listen for responses to a command.
	 * This allows multiple clients to register handlers for the same command type.
	 */
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	// oxlint-disable-next-line typescript-eslint/no-explicit-any
	private handlers = new Map<string, CommandHandler<any, any>>();

	constructor(private transport: Transport) {}

	/**
	 * Register a handler for a specific command type for a specific client.
	 *
	 * @description When a command of type {@link CommandClass} is received, the handler will be invoked in a 1-1 manner.
	 * This is to ensure the result of the command invocation can be cleanly passed to the callee.
	 *
	 * @param clientId The client identifier that this handler is registered for
	 * @param CommandClass The command class to register a handler for
	 * @param handler The handler function to invoke when the command is received
	 * @returns void
	 */
	public register<
		T extends CommandConstructor,
		C extends CommandProperties = ExtractCommandProperties<T>,
		R extends CommandResult = ExtractCommandResult<T>,
	>(clientId: string, CommandClass: T, handler: CommandHandler<C, R>): void {
		const handlerKey = this.createHandlerKey(clientId, CommandClass.identifier);
		this.handlers.set(handlerKey, handler);
	}

	/**
	 * Unregisters a handler for a specific command type for a specific client.
	 */
	public unregister<T extends CommandConstructor>(clientId: string, CommandClass: T): void {
		const handlerKey = this.createHandlerKey(clientId, CommandClass.identifier);
		this.handlers.delete(handlerKey);
	}

	/**
	 * Unregisters all handlers for a specific client.
	 *
	 * @param clientId The client identifier to remove all handlers for
	 * @returns number of handlers removed
	 */
	public unregisterAll(clientId: string): number {
		const keysToDelete: string[] = [];
		const prefix = `${clientId}:`;

		// find all relevant keys
		for (const key of this.handlers.keys()) {
			if (key.startsWith(prefix)) {
				keysToDelete.push(key);
			}
		}

		// drop them
		for (const key of keysToDelete) {
			this.handlers.delete(key);
		}

		return keysToDelete.length;
	}

	/**
	 * Invokes the appropriate handler for a given {@link RPCRequest}, and yields the result via
	 * {@link transport} to the appropriate JavaScript context. If the handler throws, propagates an
	 * {@link RPCResponse} with an error body. Vice versa for a successful handler response.
	 *
	 * @param request incoming RPC request (delegated method call, as a command)
	 */
	public async handleRequest(request: RPCRequest): Promise<void> {
		const params = request.params as CommandProperties;
		const handlerKey = this.createHandlerKey(params.receiverId, request.method);
		const handler = this.handlers.get(handlerKey);

		emitter().emit({
			type: 'command-received',
			commandId: request.id,
			commandType: request.method,
			senderId: params.senderId,
			receiverId: params.receiverId,
			properties: { ...params },
			groupId: params.groupId,
		});

		if (!handler) {
			const err = `handler not found for ${request.method} with clientId ${params.receiverId}`;
			console.warn(err);

			// emit command failed event
			emitter().emit({
				type: 'command-failed',
				commandId: request.id,
				commandType: request.method,
				senderId: params.senderId,
				receiverId: params.receiverId,
				error: {
					message: err,
					code: 'METHOD_NOT_FOUND',
				},
				groupId: params.groupId,
			});

			// communicate to sender that handler failed
			this.sendResponse(
				request,
				rpcError(
					request.id,
					params.receiverId,
					params.senderId,
					JSONRPCErrorCode.MethodNotFound,
					new Error(err),
				),
			);
			return;
		}

		const startTime = performance.now();

		try {
			const result = await handler(request.params);
			const duration = performance.now() - startTime;

			// emit command completed event
			emitter().emit({
				type: 'command-completed',
				commandId: request.id,
				commandType: request.method,
				senderId: params.senderId,
				receiverId: params.receiverId,
				result,
				duration,
				groupId: params.groupId,
			});

			this.sendResponse(
				request,
				rpcSuccess(request.id, params.receiverId, params.senderId, result),
			);
		} catch (error) {
			const err = error instanceof Error ? error : new Error(String(error));
			const duration = performance.now() - startTime;

			// emit command failed event
			emitter().emit({
				type: 'command-failed',
				commandId: request.id,
				commandType: request.method,
				senderId: params.senderId,
				receiverId: params.receiverId,
				error: {
					message: err.message,
					code: 'HANDLER_ERROR',
					stack: err.stack,
				},
				duration,
				groupId: params.groupId,
			});

			this.sendResponse(
				request,
				rpcError(
					request.id,
					params.receiverId,
					params.senderId,
					JSONRPCErrorCode.InternalError,
					err,
				),
			);

			console.error(err);
		}
	}

	private sendResponse(request: RPCRequest, response: RPCResponse): void {
		if (fg('rovo_bridge_validate_message_origin')) {
			this.transport.send(response, request);
		} else {
			this.transport.send(response);
		}
	}

	/**
	 * Directly invoke a handler of a specific command for a specific client.
	 */
	public async invoke<C extends CommandProperties, R extends CommandResult>(
		command: CommandConstructor<C, R>,
		params: C,
	): Promise<R | undefined> {
		const handlerKey = this.createHandlerKey(params.receiverId, command.identifier);
		const handler = this.handlers.get(handlerKey);

		emitter().emit({
			type: 'command-received',
			commandId: params._invocationId ?? 'local-invocation',
			commandType: command.identifier,
			senderId: params.senderId,
			receiverId: params.receiverId,
			properties: params,
			groupId: params.groupId,
		});

		if (!handler) {
			const err = `handler not found for command ${command.identifier} with clientId ${params.receiverId}`;
			console.error(err);

			// emit command failed event
			emitter().emit({
				type: 'command-failed',
				commandId: params._invocationId ?? 'local-invocation',
				commandType: command.identifier,
				senderId: params.senderId,
				receiverId: params.receiverId,
				error: {
					message: err,
					code: 'METHOD_NOT_FOUND',
				},
				groupId: params.groupId,
			});

			return undefined;
		}

		const startTime = performance.now();

		try {
			const result = handler?.(params);
			const duration = performance.now() - startTime;

			// emit command completed event
			emitter().emit({
				type: 'command-completed',
				commandId: params._invocationId || 'local-invocation',
				commandType: command.identifier,
				senderId: params.senderId,
				receiverId: params.receiverId,
				result,
				duration,
				groupId: params.groupId,
			});

			return result;
		} catch (error) {
			const err = error instanceof Error ? error : new Error(String(error));
			const duration = performance.now() - startTime;

			// emit command failed event
			emitter().emit({
				type: 'command-failed',
				commandId: params._invocationId || 'local-invocation',
				commandType: command.identifier,
				senderId: params.senderId,
				receiverId: params.receiverId,
				error: {
					message: err.message,
					code: 'HANDLER_ERROR',
					stack: err.stack,
				},
				duration,
				groupId: params.groupId,
			});

			throw err;
		}
	}

	/**
	 * Clear all relevant state for handling of responses.
	 */
	public destroy(): void {
		this.handlers.clear();
	}

	/**
	 * Creates a unique key for the handler mapping using clientId and command name.
	 */
	private createHandlerKey(clientId: string, commandName: string): string {
		return `${clientId}:${commandName}`;
	}
}
